import { SearchOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Divider, Empty, Flex, Input, List, Modal, Skeleton, Typography } from 'antd';
import { debounce } from 'lodash';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { addTopicMember, deleteTopicMember } from 'src/apis/helpdesk/topic.api';
import { getEmployeUsersDropdown } from 'src/apis/master_data/employee.api';
import XIcon from 'src/assets/icons/close.png';
import AvatarCustom from 'src/components/v2/Avatar';
import Icon from 'src/components/v2/Icon';
import { InviteTypeEn } from 'src/constants/inviteType.enum';
import { useChatStore } from 'src/stores/chatStore';
import { ITopicMember, TopicMemberAdd } from 'src/types/helpdesk/topic.type';
import { IEmployeeUser } from 'src/types/master_data/employeeUser.type';
import { showNotification } from 'src/utils/notification';
import { getFirstLetters } from 'src/utils/utils';
import './TopicMember.scss';

const { Text } = Typography;

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

const AddTopicMember = ({ open, setOpen }: Props) => {
  const { id } = useParams();
  const [employees, setEmployees] = useState<IEmployeeUser[]>([]);
  const [searchEmployee, setSearchEmployee] = useState<string | undefined>(undefined);

  // ✅ Get members from store instead of context
  const { rooms, updateRoomPartial } = useChatStore();
  const currentRoom = id ? rooms[id] : null;
  const members = currentRoom?.topicMembers || [];

  const employeeUsersDropdownQuery = useQuery({
    queryKey: [
      'employeeUsersDropdown',
      searchEmployee,
      members?.map((item) => item.employeeObj?.id).join(',') // ← Use store members
    ],
    queryFn: () =>
      getEmployeUsersDropdown(
        searchEmployee,
        members?.map((item) => item.employeeObj?.id).join(',') // ← Use store members
      ),
    staleTime: 60 * 1000,
    enabled: !!searchEmployee,
    onSuccess: (data) => {
      setEmployees(data.data);
    }
  });

  const addTopicMemberMutation = useMutation({
    mutationFn: (data: TopicMemberAdd[]) => addTopicMember(id ?? '', data),
    onSuccess: (response) => {
      console.log('✅ Member added successfully:', response.data);

      // ✅ Update store with new members
      if (id && response?.data) {
        const newMembers = response.data as ITopicMember[];
        const updatedMembers = [...members, ...newMembers];

        updateRoomPartial(id, {
          topicMembers: updatedMembers
        });
      }
    },
    onError: (error) => {
      console.error('❌ Add member failed:', error);
    }
  });

  const deleteTopicMemberMutation = useMutation({
    mutationFn: (memberId: string) => deleteTopicMember(memberId),
    onSuccess: (_, deletedMemberId) => {
      console.log('✅ Member deleted successfully:', deletedMemberId);

      // ✅ Update store by removing deleted member
      if (id) {
        const updatedMembers = members.filter((member) => member?.id !== deletedMemberId);

        updateRoomPartial(id, {
          topicMembers: updatedMembers
        });
      }
    },
    onError: (error) => {
      console.error('❌ Delete member failed:', error);
    }
  });

  const handleAdd = (listTopicMember: TopicMemberAdd[]) => {
    addTopicMemberMutation.mutate(listTopicMember);
  };

  const handleDelete = (id: string, inviteType: number) => {
    if (inviteType !== InviteTypeEn.Guest) {
      showNotification('Không thể xóa thành viên này', 'warning');
      return;
    }
    deleteTopicMemberMutation.mutate(id);
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((term) => {
        if (!term) {
          setEmployees([]);
          setSearchEmployee(undefined);
        } else {
          setSearchEmployee(term);
        }
      }, 500),
    []
  );

  // ✅ Reset search when modal closes
  const handleModalClose = () => {
    setOpen(false);
    setSearchEmployee(undefined);
    setEmployees([]);
  };

  return (
    <Modal
      title={<p style={{ textAlign: 'center' }}>Thêm thành viên</p>}
      width={650}
      open={open}
      centered
      styles={{ body: { paddingBlock: 16 } }}
      onCancel={handleModalClose}
      footer={false}
      className='add-member'
    >
      <Input
        allowClear
        prefix={<SearchOutlined className='search-icon' />}
        className='add-member-input'
        placeholder='Tìm kiếm thành viên'
        onChange={(event) => debouncedSearch(event.target.value)}
      />
      <div className='member'>
        <Text strong className='member-heading'>
          Thành viên
        </Text>
        <ul className='member-list'>
          {members?.map((item) => (
            <li className='member-item' key={item.id}>
              <AvatarCustom
                size={26}
                background={item?.gradientBackground?.backgroundImage}
                text={item?.id === '0' ? 'All' : getFirstLetters(String(item.employeeObj?.fullName))}
              />
              <Flex gap={4}>
                <p className='member-name'>{item.employeeObj?.fullName}</p>
                {item.userName && <p className='member-name'>({item.userName ?? ''})</p>}
              </Flex>
              <Icon
                url={XIcon}
                size={16}
                className='member-remove'
                onClick={() => handleDelete(item.id, item.inviteType)}
              />
            </li>
          ))}
        </ul>
      </div>
      <Divider />
      <div className='employee'>
        <Text strong className='employee-heading'>
          Kết quả tìm kiếm
        </Text>
        <List
          size='small'
          className='employee-list'
          dataSource={employees}
          locale={{
            emptyText: (
              <Empty
                image='https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg'
                imageStyle={{ height: 150 }}
                description=''
              />
            )
          }}
          renderItem={(item) => (
            <List.Item className='employee-item'>
              <Skeleton avatar active paragraph={{ rows: 0 }} loading={employeeUsersDropdownQuery.isFetching}>
                <List.Item.Meta
                  avatar={<AvatarCustom size={40} text={getFirstLetters(String(item.employee.fullName))} />}
                  title={
                    <Flex gap={4}>
                      <Text strong>{`${item.employee.fullName}`}</Text>
                      {item.userName && <Text type='secondary'>({item.userName})</Text>}
                    </Flex>
                  }
                  description={
                    <Text style={{ fontSize: 13 }} type='secondary'>
                      {item.employee.department?.name}
                    </Text>
                  }
                />
                <Button
                  type='primary'
                  size='small'
                  onClick={() => {
                    handleAdd([
                      {
                        employeeCode: item.employee.code,
                        userName: item.userName,
                        inviteType: InviteTypeEn.Guest,
                        status: 1
                      }
                    ]);
                  }}
                  loading={
                    addTopicMemberMutation?.variables?.[0].employeeCode === item.employee.code &&
                    addTopicMemberMutation.isLoading
                  }
                  disabled={
                    // ✅ Disable if already a member
                    members.some((member) => member.employeeObj?.code === item.employee.code)
                  }
                >
                  Thêm
                </Button>
              </Skeleton>
            </List.Item>
          )}
        />
      </div>
    </Modal>
  );
};

export default AddTopicMember;
